/**
 * 计分系统 v2（纯逻辑，无浏览器依赖，node 可直接测试）。
 * 设计依据 docs/01-design-research.md：东方骨架 + Cave 短周期结算。
 * 核心资源：pointV（可成长的点道具价值）+ chapter 结算。
 * 兼容说明：保留 v1 的 multiplier/maxMultiplier/bumpMultiplier 作为显示用 shim
 * （HUD 倍率显示 = pointV/pointBase），机制层无需改动。
 */
export const SCORE_CONFIG = {
  // 点道具
  pointBase: 10_000,        // pointV 初始值
  pointMax: 50_000,         // pointV 上限（Normal）
  pocLineY: 120,            // 满值线 = POC 线
  bottomFactor: 0.2,        // 版底价值系数
  // 擦弹
  grazeScore: 100,
  grazeRadius: 16,
  grazePerBulletMax: 5,
  // 星道具（消弹转化）
  starBase: 300,
  starPerGraze: 10,         // 每 40 擦弹 +10
  starGrazeStep: 40,
  starFromBomb: 100,
  // 符卡
  spellBasePerStage: 1_000_000,
  spellBaseDifficulty: 1_000_000,
  spellGracePeriod: 5,      // 秒
  spellDecayFactor: 2 / 3,
  spellGrazeRefill: 5_000,
  // EBB（阶段击破消弹奖，永夜抄公式）
  ebbFirst: 2_000,
  ebbStep: 20,
  ebbCap: 8_000,
  // chapter
  chapterGrazeWeight: 50,
  chapterFragThreshold: 1_000_000,
  chapterPointVStep: 10,    // chapter 分每 5 万 pointV +10
  chapterPointVPer: 50_000,
  // 面结算
  stageTallyBase: 1_000_000,
  stageTallyPerPointItem: 50_000,
  stageTallyPerGraze: 500,
  finalLifeBonus: 25_000_000,
  finalBombBonus: 5_000_000,
  // 奖残机（按点道具累计数）：一关全收（~250 点）不足 1 命，全程满收约 4-5 命
  extendItems: [300, 600, 1000, 1500, 2200, 3000],
  extendItemsRepeat: 1000,
  // 通关结算（兼容字段）
  clearLifeBonus: 50_000,
  clearBombBonus: 10_000,
  extendScore: 5_000_000,   // 兼容字段（v2 用 extendItems）
};

export class ScoreSystem {
  constructor(config = SCORE_CONFIG) {
    this.cfg = config;
    this.reset();
  }

  reset() {
    this.score = 0;
    /** 难度分数系数（gameScene 注入，1 = Normal 基准） */
    this.scoreMul = 1;
    this.pointV = this.cfg.pointBase;
    this.graze = 0;
    this.pointItems = 0;          // 点道具累计（奖残机依据）
    this.killStreak = 0;
    this.spellCardsCaptured = 0;
    this.spellCardsAttempted = 0;
    this._grazedCount = new Map(); // bulletId -> 次数（每弹最多 grazePerBulletMax 次）
    this._extendsGiven = 0;
    // chapter 状态
    this.chapterGraze = 0;
    this.chapterKills = 0;
    this.chapterEnemies = 0;
    this._chapterScoreStart = 0;
    // 符卡状态
    this._spell = null; // {initial, current, timeLimit, elapsed, stageIndex}
  }

  /**
   * 得分入口：所有加分统一经此按难度分数系数缩放，返回实际得分（显示用）。
   * @param {number} v 基准分
   * @returns {number}
   */
  gain(v) {
    const g = Math.round(v * this.scoreMul);
    this.score += g;
    return g;
  }

  // ---------- 点道具 ----------

  /** 高度系数：POC 线上 1.0，线下线性衰减至版底 bottomFactor。 */
  heightFactor(y, viewH) {
    if (y <= this.cfg.pocLineY) return 1.0;
    const t = Math.min(1, (y - this.cfg.pocLineY) / (viewH - this.cfg.pocLineY));
    return 1 - t * (1 - this.cfg.bottomFactor);
  }

  /** 点道具当前价值（y 处）。 */
  pointItemValue(y, viewH) {
    return Math.round(this.pointV * this.heightFactor(y, viewH));
  }

  /** 拾取点道具，返回得分。 */
  collectPointItem(y, viewH) {
    const v = this.pointItemValue(y, viewH);
    const g = this.gain(v);
    this.pointItems++;
    return g;
  }

  /** 星道具（消弹转化）价值。fromBomb=true 时固定低价（防 bomb 刷分）。 */
  starItemValue(fromBomb = false) {
    if (fromBomb) return this.cfg.starFromBomb;
    return this.cfg.starBase + this.cfg.starPerGraze * Math.floor(this.graze / this.cfg.starGrazeStep);
  }

  /** 拾取星道具，返回得分。 */
  collectStar(fromBomb = false) {
    const v = this.starItemValue(fromBomb);
    return this.gain(v);
  }

  // ---------- 击杀 / 擦弹 ----------

  registerKill(baseScore) {
    this.killStreak++;
    this.chapterKills++;
    const v = Math.round(baseScore * (this.pointV / this.cfg.pointBase));
    return this.gain(v);
  }

  /** 章内敌人总数（击破率分母），由关卡脚本上报。 */
  registerEnemySpawn(n = 1) {
    this.chapterEnemies += n;
  }

  /** 擦弹：每弹最多 N 次。返回是否新擦弹。 */
  registerGraze(bulletId) {
    const c = this._grazedCount.get(bulletId) ?? 0;
    if (c >= this.cfg.grazePerBulletMax) return false;
    this._grazedCount.set(bulletId, c + 1);
    this.graze++;
    this.chapterGraze++;
    this.gain(this.cfg.grazeScore);
    if (this._spell) {
      this._spell.current = Math.min(this._spell.initial, this._spell.current + this.cfg.spellGrazeRefill);
    }
    return true;
  }

  onPlayerDeath() {
    this.killStreak = 0;
    this.chapterGraze = 0;
    this._grazedCount.clear();
    // pointV 不清零：留重建路径（调研结论 5）
  }

  // ---------- chapter（短周期结算） ----------

  startChapter() {
    this.chapterGraze = 0;
    this.chapterKills = 0;
    this.chapterEnemies = 0;
    this._chapterScoreStart = this.score;
  }

  /**
   * 章结算。返回 {bonus, bombFragment, chapterScore}。
   * bonus = 章内擦弹 × 击破率 × 权重；chapter 分每 chapterPointVPer 给 pointV +chapterPointVStep。
   */
  endChapter() {
    const killRatio = this.chapterEnemies > 0 ? Math.min(1, this.chapterKills / this.chapterEnemies) : 1;
    const rawBonus = Math.round(this.chapterGraze * killRatio * this.cfg.chapterGrazeWeight);
    const bonus = this.gain(rawBonus);
    const chapterScore = this.score - this._chapterScoreStart;
    this.bumpPointV(this.cfg.chapterPointVStep * Math.floor(chapterScore / this.cfg.chapterPointVPer));
    return { bonus, bombFragment: bonus >= this.cfg.chapterFragThreshold, chapterScore };
  }

  // ---------- 符卡 ----------

  /** 符卡阶段开始：初值 = 面数×基数 + 难度×基数。 */
  spellCardStart(stageIndex, timeLimit, difficulty = 1) {
    const initial = this.cfg.spellBasePerStage * stageIndex + this.cfg.spellBaseDifficulty * difficulty;
    this._spell = { initial, current: initial, timeLimit, elapsed: 0 };
  }

  /** 每帧调用：5 秒宽限后按 (2/3)×初值/(时限-5) 每秒衰减。 */
  spellCardTick(dt) {
    if (!this._spell) return;
    this._spell.elapsed += dt;
    const s = this._spell;
    if (s.elapsed > this.cfg.spellGracePeriod && s.timeLimit > this.cfg.spellGracePeriod) {
      s.current = Math.max(0, s.current - (this.cfg.spellDecayFactor * s.initial * dt) / (s.timeLimit - this.cfg.spellGracePeriod));
    }
  }

  /** 当前符卡 bonus 面值（HUD 显示用）。 */
  spellCardValue() {
    return this._spell ? Math.round(this._spell.current) : 0;
  }

  /**
   * 符卡阶段结束结算。capture 条件：限时内 + 无伤 + 无 bomb。
   * 兼容 v1 签名。返回 bonus（0=未 capture）。
   */
  resolveSpellCard({ noMiss, noBomb, inTime }) {
    this.spellCardsAttempted++;
    const s = this._spell;
    this._spell = null;
    if (!s || !(noMiss && noBomb && inTime)) return 0;
    this.spellCardsCaptured++;
    return this.gain(Math.round(s.current));
  }

  /** EBB：阶段击破时按屏上敌弹数结算（永夜抄公式）。 */
  enemyBulletBonus(bulletCount) {
    let total = 0;
    for (let n = 1; n <= bulletCount; n++) {
      total += Math.min(this.cfg.ebbCap, this.cfg.ebbFirst + this.cfg.ebbStep * (n - 1));
    }
    // 消弹同时喂养 pointV：每弹 +2
    this.bumpPointV(bulletCount * 2);
    return this.gain(total);
  }

  // ---------- pointV 成长 ----------

  bumpPointV(delta) {
    this.pointV = Math.min(this.cfg.pointMax, Math.round((this.pointV + delta) / 10) * 10);
  }

  // ---------- 结算 / 奖残机 ----------

  /** 面结算。isFinal 时追加残机/炸弹大奖。 */
  stageTally({ stageIndex, pointItems, graze, isFinal = false, lives = 0, bombs = 0 }) {
    let v = stageIndex * this.cfg.stageTallyBase
      + pointItems * this.cfg.stageTallyPerPointItem
      + graze * this.cfg.stageTallyPerGraze;
    if (isFinal) v += lives * this.cfg.finalLifeBonus + bombs * this.cfg.finalBombBonus;
    return this.gain(v);
  }

  /** 奖残机：按点道具累计数（妖妖梦曲线），返回新获得数。 */
  checkExtend() {
    const th = this.cfg.extendItems;
    let earned = th.filter((t) => this.pointItems >= t).length;
    if (this.pointItems >= th[th.length - 1]) {
      earned += Math.floor((this.pointItems - th[th.length - 1]) / this.cfg.extendItemsRepeat);
    }
    const n = earned - this._extendsGiven;
    this._extendsGiven = Math.max(this._extendsGiven, earned);
    return Math.max(0, n);
  }

  // ---------- v1 兼容 shim ----------

  /** HUD 倍率显示 = pointV / 初始值。 */
  get multiplier() {
    return +(this.pointV / this.cfg.pointBase).toFixed(2);
  }

  get maxMultiplier() {
    return +(this.pointV / this.cfg.pointBase).toFixed(2);
  }

  bumpMultiplier(_delta) { /* v2 由 bumpPointV 取代，保留防旧调用报错 */ }

  perfectBossBonus() {
    this.bumpPointV(2_000);
    return 0;
  }

  clearBonus(lives, bombs) {
    const v = lives * this.cfg.clearLifeBonus + bombs * this.cfg.clearBombBonus;
    return this.gain(v);
  }

  summary() {
    return {
      score: this.score,
      graze: this.graze,
      maxMultiplier: this.maxMultiplier,
      pointV: this.pointV,
      pointItems: this.pointItems,
      spellCards: `${this.spellCardsCaptured}/${this.spellCardsAttempted}`,
    };
  }
}
